import queryString from "query-string"
import { userEndpoints } from "store/apis/userAPI/user.api"
import { IUserStatistics } from "store/apis/userAPI/user.types"
import { missionsApi } from "store/apis/missionsAPI/missions.api"
import { PROFESSION_TYPES } from "store/apis/professionsAPI/values"
import { balanceEndpoints } from "store/apis/balanceAPI/balance.api"
import { IPaginationReq } from "utils/interfaces"
import { TAGS, api } from "../../api"
import {
  IBreedRunesResponse,
  IRuneInstance,
  IRunesFilters,
  IUserRunesResponse
} from "./types"

export const runesApi = api.enhanceEndpoints({
  addTagTypes: [TAGS.RUNES, TAGS.PROFESSIONS, TAGS.USER, TAGS.MISSIONS]
})

export const toolsEndpoints = runesApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllRunes: builder.query<IUserRunesResponse, IPaginationReq>({
      query: ({ page, limit }) => ({
        url: `/runes/rune-instances?page=${page}&limit=${limit}`,
        method: "GET"
      }),
      providesTags: (
        result,
        _,
        params
      ): readonly { type: typeof TAGS.RUNES; id: string }[] =>
        result
          ? [
              {
                type: TAGS.RUNES as const,
                id: `rune-list:page:${params.page}:limit:${params.limit}`
              },
              { type: TAGS.RUNES as const, id: "USER_RUNES_LIST" }
            ]
          : [{ type: TAGS.RUNES as const, id: "USER_RUNES_LIST" }]
    }),
    getFilteredRunes: builder.query<
      IUserRunesResponse,
      IPaginationReq & IRunesFilters
    >({
      query: ({ page, limit, ...filters }) => ({
        url: `/runes/rune-instances?${queryString.stringify(
          { page, limit, ...filters },
          {
            skipNull: false,
            skipEmptyString: true
          }
        )}`,
        method: "GET"
      }),
      providesTags: (
        result,
        _,
        params
      ): readonly { type: typeof TAGS.RUNES; id: string }[] =>
        result
          ? [
              {
                type: TAGS.RUNES as const,
                id: `runes-list:page:${params.page}:limit:${params.limit}`
              },
              { type: TAGS.RUNES as const, id: "FILTERED_USER_RUNE_LIST" }
            ]
          : [{ type: TAGS.RUNES as const, id: "FILTERED_USER_RUNE_LIST" }]
    }),
    getEquippedRunes: builder.query<IRuneInstance[], { toolId: string }>({
      query: ({ toolId }) => ({
        url: `/runes/equipped-runes/${toolId}`,
        method: "GET"
      }),
      providesTags: (
        result,
        _,
        params
      ): readonly { type: typeof TAGS.RUNES; id: string }[] =>
        result
          ? [
              {
                type: TAGS.RUNES as const,
                id: `runes-in-tool:${params.toolId}`
              }
            ]
          : [{ type: TAGS.RUNES as const, id: "TOOL_RUNES_LIST" }]
    }),
    breedRunes: builder.mutation<
      IBreedRunesResponse,
      { runesToBreed: string[] }
    >({
      query: ({ runesToBreed }) => ({
        url: `/runes/fuse`,
        method: "PATCH",

        data: { runesToBreed }
      }),
      extraOptions: { maxRetries: 2 },
      invalidatesTags: () => [
        { type: TAGS.PROFESSIONS, id: PROFESSION_TYPES.SCOUT },
        { type: TAGS.RUNES }
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          userEndpoints.util.updateQueryData(
            "getUserGameStatistics",
            undefined,
            (draft: IUserStatistics) => {
              draft.totalRunesCraftedTries += 1
              draft.totalRunesSuccessesCrafted += data?.createdRune ? 1 : 0
            }
          )
        )

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )

        dispatch(
          missionsApi.util.invalidateTags([
            { type: TAGS.MISSIONS, id: "MY_PROGRESS" },
            { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
          ])
        )
      }
    })
  })
})
